import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { baseApiURL } from '../../baseUrl';
import {
    FiCalendar, FiPlus, FiMapPin, FiAward, FiEdit2, FiX,
    FiCheckCircle, FiUsers, FiChevronDown, FiChevronUp,
    FiExternalLink, FiLoader, FiTrash2
} from 'react-icons/fi';

const EMPTY_FORM = {
    company: '', title: '', driveDate: '', registrationDeadline: '',
    mode: 'Offline', minCGPA: 0, min10thPercentage: 0, min12thPercentage: 0,
    packageDetails: '', venue: '', description: '', status: 'Upcoming'
};

const STATUS_COLORS = {
    Upcoming:  'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    Ongoing:   'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    Completed: 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    Cancelled: 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
};

const Drives = () => {
    const [drives, setDrives] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Edit modal state
    const [editingDrive, setEditingDrive] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);

    // Selected students panel state
    const [expandedDriveId, setExpandedDriveId] = useState(null);
    const [selectedStudents, setSelectedStudents] = useState({});
    const [loadingStudents, setLoadingStudents] = useState(false);

    useEffect(() => {
        fetchDrives();
        fetchCompanies();
    }, []);

    const fetchDrives = async () => {
        try {
            const res = await axios.get(`${baseApiURL()}/placement/drives/get`);
            if (res.data.success) setDrives(res.data.drives || []);
        } catch (error) { console.error(error); }
    };

    const fetchCompanies = async () => {
        try {
            const res = await axios.get(`${baseApiURL()}/placement/companies/get`);
            if (res.data.success) setCompanies(res.data.companies || []);
        } catch (error) { console.error(error); }
    };

    const fetchSelectedStudents = async (driveId) => {
        if (selectedStudents[driveId]) return; // already fetched
        setLoadingStudents(true);
        try {
            const res = await axios.get(`${baseApiURL()}/placement/applications/drive/${driveId}`);
            if (res.data.success) {
                const sel = (res.data.applications || []).filter(a => a.status === 'Selected');
                setSelectedStudents(prev => ({ ...prev, [driveId]: sel }));
            }
        } catch (err) {
            toast.error('Failed to fetch selected students');
        } finally {
            setLoadingStudents(false);
        }
    };

    const toggleSelectedPanel = (driveId) => {
        if (expandedDriveId === driveId) {
            setExpandedDriveId(null);
        } else {
            setExpandedDriveId(driveId);
            fetchSelectedStudents(driveId);
        }
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            eligibilityCriteria: {
                minCGPA: formData.minCGPA,
                min10thPercentage: formData.min10thPercentage,
                min12thPercentage: formData.min12thPercentage
            }
        };
        try {
            const res = await axios.post(`${baseApiURL()}/placement/drives/add`, payload);
            if (res.data.success) {
                toast.success(res.data.message);
                fetchDrives();
                setFormData(EMPTY_FORM);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error adding drive');
        }
    };

    const openEdit = (drive) => {
        setEditingDrive(drive);
        setEditForm({
            company: drive.company?._id || drive.company || '',
            title: drive.title || '',
            driveDate: drive.driveDate ? drive.driveDate.slice(0, 10) : '',
            registrationDeadline: drive.registrationDeadline ? drive.registrationDeadline.slice(0, 10) : '',
            mode: drive.mode || 'Offline',
            minCGPA: drive.eligibilityCriteria?.minCGPA || 0,
            min10thPercentage: drive.eligibilityCriteria?.min10thPercentage || 0,
            min12thPercentage: drive.eligibilityCriteria?.min12thPercentage || 0,
            packageDetails: drive.packageDetails || '',
            venue: drive.venue || '',
            description: drive.description || '',
            status: drive.status || 'Upcoming',
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...editForm,
            eligibilityCriteria: {
                minCGPA: editForm.minCGPA,
                min10thPercentage: editForm.min10thPercentage,
                min12thPercentage: editForm.min12thPercentage
            }
        };
        try {
            const res = await axios.put(`${baseApiURL()}/placement/drives/update/${editingDrive._id}`, payload);
            if (res.data.success) {
                toast.success('Drive updated successfully');
                fetchDrives();
                setEditingDrive(null);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating drive');
        }
    };

    const handleStatusChange = async (driveId, newStatus) => {
        try {
            const res = await axios.put(`${baseApiURL()}/placement/drives/update/${driveId}`, { status: newStatus });
            if (res.data.success) {
                toast.success(`Drive marked as ${newStatus}`);
                setDrives(prev => prev.map(d => d._id === driveId ? { ...d, status: newStatus } : d));
            }
        } catch (error) {
            toast.error('Error updating status');
        }
    };

    const handleDelete = async (driveId) => {
        if (!window.confirm('Delete this drive? This cannot be undone.')) return;
        try {
            const res = await axios.delete(`${baseApiURL()}/placement/drives/delete/${driveId}`);
            if (res.data.success) {
                toast.success('Drive deleted');
                fetchDrives();
            }
        } catch {
            toast.error('Error deleting drive');
        }
    };

    const getFullName = (student) => {
        if (!student) return 'N/A';
        return [student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ') || 'N/A';
    };

    const inputCls = "w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500";
    const labelCls = "text-[10px] font-bold uppercase text-slate-400";

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                <div className="bento-header-banner flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <FiCalendar className="text-xl" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Placement Drives</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                                Schedule, edit, track status, and view selected students per drive.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Add Drive Form */}
                    <form onSubmit={handleSubmit} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl space-y-4">
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                            <FiPlus className="text-indigo-600 dark:text-indigo-400" />
                            <span>Schedule New Placement Drive</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <label className={labelCls}>Company *</label>
                                <select name="company" value={formData.company} onChange={handleChange} required className={inputCls}>
                                    <option value="">Select Company</option>
                                    {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Job Role / Position *</label>
                                <input type="text" name="title" placeholder="e.g. Software Engineer" value={formData.title} onChange={handleChange} required className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Drive Date *</label>
                                <input type="date" name="driveDate" value={formData.driveDate} onChange={handleChange} required className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Registration Deadline *</label>
                                <input type="date" name="registrationDeadline" value={formData.registrationDeadline} onChange={handleChange} required className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Drive Mode</label>
                                <select name="mode" value={formData.mode} onChange={handleChange} className={inputCls}>
                                    <option value="Offline">Offline (Campus)</option>
                                    <option value="Online">Online / Virtual</option>
                                    <option value="Hybrid">Hybrid</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Venue</label>
                                <input type="text" name="venue" placeholder="e.g. Seminar Hall A" value={formData.venue} onChange={handleChange} className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Min CGPA Required</label>
                                <input type="number" step="0.1" name="minCGPA" placeholder="e.g. 7.0" value={formData.minCGPA} onChange={handleChange} className={inputCls} />
                            </div>
                            <div className="space-y-1">
                                <label className={labelCls}>Package (CTC)</label>
                                <input type="text" name="packageDetails" placeholder="e.g. 6 LPA" value={formData.packageDetails} onChange={handleChange} className={inputCls} />
                            </div>
                        </div>
                        <div className="flex justify-end pt-1">
                            <button type="submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer flex items-center gap-1.5">
                                <FiPlus /> <span>Schedule Drive</span>
                            </button>
                        </div>
                    </form>

                    {/* Drives List */}
                    <div className="space-y-4">
                        {drives.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 text-xs font-semibold">
                                No placement drives scheduled yet.
                            </div>
                        ) : (
                            drives.map(drive => (
                                <div key={drive._id} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden hover:shadow-sm transition">
                                    {/* Drive Card */}
                                    <div className="p-4 flex flex-col md:flex-row md:items-start justify-between gap-4">
                                        {/* Left: Info */}
                                        <div className="space-y-2 flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h3 className="font-bold text-sm text-slate-900 dark:text-white">{drive.title}</h3>
                                                <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                                                    @ {drive.company?.name || 'Corporate Partner'}
                                                </span>
                                                {drive.packageDetails && (
                                                    <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-full text-[10px] font-bold">
                                                        💰 {drive.packageDetails}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                                                <span className="flex items-center gap-1">
                                                    <FiCalendar size={11} />
                                                    Drive: {drive.driveDate ? new Date(drive.driveDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                </span>
                                                {drive.registrationDeadline && (
                                                    <span className="flex items-center gap-1">
                                                        ⏰ Deadline: {new Date(drive.registrationDeadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <FiMapPin size={11} />
                                                    {drive.mode}{drive.venue ? ` — ${drive.venue}` : ''}
                                                </span>
                                                {drive.eligibilityCriteria?.minCGPA > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <FiAward size={11} />
                                                        Min CGPA: {drive.eligibilityCriteria.minCGPA}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right: Controls */}
                                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                                            {/* Status select */}
                                            <select
                                                value={drive.status || 'Upcoming'}
                                                onChange={(e) => handleStatusChange(drive._id, e.target.value)}
                                                className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border cursor-pointer outline-none focus:ring-2 focus:ring-indigo-500 ${STATUS_COLORS[drive.status] || STATUS_COLORS.Upcoming}`}
                                            >
                                                <option value="Upcoming">Upcoming</option>
                                                <option value="Ongoing">Ongoing</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>

                                            {/* Selected students toggle */}
                                            <button
                                                onClick={() => toggleSelectedPanel(drive._id)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-lg text-[10px] font-bold hover:bg-emerald-100 dark:hover:bg-emerald-900 transition cursor-pointer"
                                            >
                                                <FiCheckCircle size={11} />
                                                Selected
                                                {expandedDriveId === drive._id ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />}
                                            </button>

                                            {/* Edit button */}
                                            <button
                                                onClick={() => openEdit(drive)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-lg text-[10px] font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900 transition cursor-pointer"
                                            >
                                                <FiEdit2 size={11} /> Edit
                                            </button>

                                            {/* Delete button */}
                                            <button
                                                onClick={() => handleDelete(drive._id)}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 rounded-lg text-[10px] font-bold hover:bg-rose-100 dark:hover:bg-rose-900 transition cursor-pointer"
                                                title="Delete Drive"
                                            >
                                                <FiTrash2 size={11} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selected Students Panel */}
                                    {expandedDriveId === drive._id && (
                                        <div className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <FiUsers className="text-emerald-600 dark:text-emerald-400" size={14} />
                                                <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                                                    Selected Students
                                                </span>
                                                {selectedStudents[drive._id] && (
                                                    <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-full text-[10px] font-bold">
                                                        {selectedStudents[drive._id].length} Selected
                                                    </span>
                                                )}
                                            </div>

                                            {loadingStudents && !selectedStudents[drive._id] ? (
                                                <div className="flex items-center gap-2 py-4 text-slate-400 text-xs">
                                                    <FiLoader className="animate-spin" size={14} />
                                                    Loading selected students...
                                                </div>
                                            ) : selectedStudents[drive._id]?.length === 0 ? (
                                                <p className="text-slate-400 text-xs font-semibold py-4 text-center">
                                                    No students have been selected for this drive yet.
                                                </p>
                                            ) : (
                                                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                                    <table className="min-w-full divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                                                        <thead className="bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                                                            <tr>
                                                                <th className="py-2.5 px-3 text-left">#</th>
                                                                <th className="py-2.5 px-3 text-left">Enrollment No</th>
                                                                <th className="py-2.5 px-3 text-left">Student Name</th>
                                                                <th className="py-2.5 px-3 text-left">Branch</th>
                                                                <th className="py-2.5 px-3 text-left">CGPA</th>
                                                                <th className="py-2.5 px-3 text-left">Phone</th>
                                                                <th className="py-2.5 px-3 text-left">Resume</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                            {(selectedStudents[drive._id] || []).map((app, idx) => (
                                                                <tr key={app._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-slate-800 dark:text-slate-200">
                                                                    <td className="py-2.5 px-3 text-slate-400 font-mono">{idx + 1}</td>
                                                                    <td className="py-2.5 px-3 font-mono font-bold text-slate-900 dark:text-white">
                                                                        {app.student?.enrollmentNo || '—'}
                                                                    </td>
                                                                    <td className="py-2.5 px-3">
                                                                        <p className="font-bold text-slate-900 dark:text-white">{getFullName(app.student)}</p>
                                                                        <p className="text-[10px] text-slate-400">{app.student?.email || ''}</p>
                                                                    </td>
                                                                    <td className="py-2.5 px-3">{app.student?.branch || '—'}</td>
                                                                    <td className="py-2.5 px-3 font-bold text-indigo-600 dark:text-indigo-400">
                                                                        {app.student?.cgpa != null ? app.student.cgpa : '—'}
                                                                    </td>
                                                                    <td className="py-2.5 px-3 text-slate-500 dark:text-slate-400">
                                                                        {app.student?.phoneNumber || '—'}
                                                                    </td>
                                                                    <td className="py-2.5 px-3">
                                                                        {app.student?.resumeLink ? (
                                                                            <a href={app.student.resumeLink} target="_blank" rel="noopener noreferrer"
                                                                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded border border-indigo-200 dark:border-indigo-800 font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900 transition text-[10px]">
                                                                                <FiExternalLink size={10} /> View
                                                                            </a>
                                                                        ) : <span className="text-slate-400 text-[10px] italic">—</span>}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Edit Drive Modal ─── */}
            {editingDrive && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        {/* Modal header */}
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
                            <div>
                                <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <FiEdit2 className="text-indigo-600 dark:text-indigo-400" size={16} />
                                    Edit Drive
                                </h2>
                                <p className="text-xs text-slate-400 mt-0.5">{editingDrive.title} @ {editingDrive.company?.name}</p>
                            </div>
                            <button
                                onClick={() => setEditingDrive(null)}
                                className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-400 transition cursor-pointer"
                            >
                                <FiX size={16} />
                            </button>
                        </div>

                        {/* Modal form */}
                        <form onSubmit={handleEditSubmit} className="p-5 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className={labelCls}>Company *</label>
                                    <select name="company" value={editForm.company} onChange={handleEditChange} required className={inputCls}>
                                        <option value="">Select Company</option>
                                        {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Job Role / Position *</label>
                                    <input type="text" name="title" value={editForm.title} onChange={handleEditChange} required className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Drive Date *</label>
                                    <input type="date" name="driveDate" value={editForm.driveDate} onChange={handleEditChange} required className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Registration Deadline *</label>
                                    <input type="date" name="registrationDeadline" value={editForm.registrationDeadline} onChange={handleEditChange} required className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Drive Mode</label>
                                    <select name="mode" value={editForm.mode} onChange={handleEditChange} className={inputCls}>
                                        <option value="Offline">Offline (Campus)</option>
                                        <option value="Online">Online / Virtual</option>
                                        <option value="Hybrid">Hybrid</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Status</label>
                                    <select name="status" value={editForm.status} onChange={handleEditChange} className={inputCls}>
                                        <option value="Upcoming">Upcoming</option>
                                        <option value="Ongoing">Ongoing</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Venue</label>
                                    <input type="text" name="venue" placeholder="e.g. Seminar Hall A" value={editForm.venue} onChange={handleEditChange} className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Package (CTC)</label>
                                    <input type="text" name="packageDetails" placeholder="e.g. 6 LPA" value={editForm.packageDetails} onChange={handleEditChange} className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Min CGPA Required</label>
                                    <input type="number" step="0.1" name="minCGPA" value={editForm.minCGPA} onChange={handleEditChange} className={inputCls} />
                                </div>
                                <div className="space-y-1">
                                    <label className={labelCls}>Min 10th %</label>
                                    <input type="number" step="0.1" name="min10thPercentage" value={editForm.min10thPercentage} onChange={handleEditChange} className={inputCls} />
                                </div>
                                <div className="md:col-span-2 space-y-1">
                                    <label className={labelCls}>Description</label>
                                    <textarea name="description" value={editForm.description} onChange={handleEditChange} rows={3} placeholder="Job description, requirements, etc." className={`${inputCls} resize-none`} />
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setEditingDrive(null)}
                                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition cursor-pointer">
                                    Cancel
                                </button>
                                <button type="submit"
                                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer flex items-center gap-1.5">
                                    <FiCheckCircle size={13} /> Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Drives;
